import {Component} from '@angular/core';
import {
    AuthorityMatrixApiService,
    AuthSessionApiService,
    AutoExchangeApiService,
    EsignApiService,
    EsignResponse,
    LegalEntity,
    Organization,
} from '@npck/auth-frontend-api';
import {AnalyticsService} from '@npck/libs/analytics-sdk';
import {
    BusinessErrorCode,
    downloadBase64,
    isWebView,
    LocalizationService,
    NO_LOGO_URL,
    RouterService,
    Spinner,
    toBusinessOrNull,
} from '@npck/libs/commons';
import {ButtonColor} from '@npck/libs/ui-kit';
import {CoidConsentDraft, OauthResponse, OauthServiceApiService} from '@npck/oauth-service-api';
import {EMPTY, finalize, map, mergeMap, Observable, switchMap} from 'rxjs';
import {AuthHolderService} from '../../domain/auth-holder.service';
import {TEMP_REDIRECT_URL} from '../../domain/constants';
import {ESignDialogService} from '../../domain/e-sign-dialog.service';
import {OauthService} from '../../domain/oauth.service';
import {
    AUTH_AUTO_CONSENT,
    AUTH_CONSENT_AGREED,
    AUTH_CONSENT_INIT,
    AUTH_REJECT,
    AUTH_SUCCEED,
    ESIGN_INIT,
    ESIGN_SIGN_REJECT,
    ESIGN_SIGN_SUCCEED,
} from '../../infra/analytics-events';
import {Path} from '../../path';
import {FinishPageReason} from '../finish-page/finish.component';

@Component({
    templateUrl: 'consent.component.html',
    styleUrls: ['consent.component.scss'],
    standalone: false,
})
export class ConsentComponent {
    Spinner = Spinner;
    scopes: string[] = [];
    clientName: string;
    externalOrgName: string;
    title: string;
    organization: Organization;
    legalEntity: LegalEntity;
    taxIncomeBin?: string;
    taxIncomePeriodYear?: number;
    taxIncomePeriodHalfYear?: number;
    providerScopes = ['account', 'account_balance', 'account_transactions', 'tax_income'];
    personalScopes = ['iin', 'full_name', 'first_name', 'last_name', 'eng_first_name', 'eng_last_name', 'middle_name', 'date_of_birth',
        'place_of_birth', 'gender', 'nationality', 'registration_address', 'document_number', 'document_type', 'document_issue_date',
        'document_expiry_date', 'document_issue_place', 'b2c2i', 'antifraud', 'family_info', 'citizenship', 'e_residency'];
    providersToScopes: ProvidersToScopes[] = [];
    NO_LOGO_URL = NO_LOGO_URL;
    INITIAL_SPINNER_ID = 'loadingData';
    cabinetUrl = TEMP_REDIRECT_URL;
    isWebView = isWebView();

    hasSignScope: boolean;
    hasPersonalScope: boolean;
    isAuthorityFlow = false;
    visibleSections: ConsentSection[] = [];
    consentChecked = false;
    personalScopeLabels: string[] = [];
    allProvidersNames = '';
    hasOrgSignScope: boolean;
    hasAuthorityConfirmationScope: boolean;
    hasAuthorityDeactivationScope: boolean;
    hasInternalAuthorityDeactivationScope: boolean;
    hasB2c2iScope: boolean;
    openidOnly = false;
    buttonAcceptLabel: string;
    buttonRejectLabel: string;
    buttonAcceptColor: ButtonColor;
    processing = false;
    clientOrganizationName: string;
    coidConsentDraft?: CoidConsentDraft;
    coidServiceCodes: string[] = [];

    signables: Signable[] = [];
    language: string;

    constructor(
        public oauthHolderService: AuthHolderService,
        private oauthServiceApiService: OauthServiceApiService,
        private localizationService: LocalizationService,
        private oauthService: OauthService,
        private analyticsService: AnalyticsService,
        private eSignDialogService: ESignDialogService,
        private esignApiService: EsignApiService,
        private authSessionApiService: AuthSessionApiService,
        private authorityMatrixApiService: AuthorityMatrixApiService,
        private routerService: RouterService,
        private autoExchangeTokenService: AutoExchangeApiService,
        private authHolderService: AuthHolderService,
    ) {
        this.language = localizationService.currentLanguage.locale;
        localizationService.language$.subscribe((language) => {
            this.language = language.locale;
        });
        this.Spinner.show(this.INITIAL_SPINNER_ID);
        if (oauthHolderService.consentData.code) {
            this.analyticsService.capture(AUTH_AUTO_CONSENT);
            this.oauthService.reportConsentAccepted().subscribe(() => this.redirectOnAccept(oauthHolderService.consentData));
            return;
        }
        this.scopes = oauthHolderService.consentData.scopes;
        this.coidConsentDraft = oauthHolderService.consentData.coidConsentDraft;
        if (this.coidConsentDraft) {
            this.coidServiceCodes = this.coidConsentDraft.serviceCodes ?? [this.coidConsentDraft.serviceCode];
        }
        this.hasSignScope = this.scopes.some(scope => scope.startsWith('esign') || scope.startsWith('organization_esign'));
        this.hasOrgSignScope = this.scopes.some(scope => scope.startsWith('organization_esign'));
        this.hasAuthorityConfirmationScope = this.scopes.some(scope => scope.startsWith('internal_authority_matrix_confirmation')
            || scope.startsWith('external_authority_matrix_confirmation'));
        this.hasAuthorityDeactivationScope = this.scopes.some(scope => scope.startsWith('external_authority_matrix_deactivation'));
        this.hasInternalAuthorityDeactivationScope = this.scopes.some(
            scope => scope.startsWith('internal_authority_matrix_deactivation'),
        );
        this.hasB2c2iScope = this.scopes.some(scope => scope.startsWith('b2c2i'));
        this.openidOnly = this.scopes.length == 1 && this.scopes[0].toLowerCase() == 'openid';
        this.organization = oauthHolderService.organization;
        this.legalEntity = oauthHolderService.authSessionData?.legalEntity;
        this.taxIncomeBin = oauthHolderService.consentData.data?.taxIncomeBin;
        this.taxIncomePeriodYear = oauthHolderService.consentData.data?.taxIncomePeriodYear;
        this.taxIncomePeriodHalfYear = oauthHolderService.consentData.data?.taxIncomePeriodHalfYear;
        this.clientName = oauthHolderService.authSessionData.clientName;
        this.clientOrganizationName = oauthHolderService.authSessionData.clientOrganizationName;
        this.personalScopes = this.personalScopes.flatMap(scopeToFilter =>
            this.scopes.filter(scope => scope.startsWith(scopeToFilter)),
        );
        this.hasPersonalScope = this.personalScopes.length > 0;
        this.isAuthorityFlow = this.hasAuthorityConfirmationScope
            || this.hasAuthorityDeactivationScope
            || this.hasInternalAuthorityDeactivationScope;
        this.analyticsService.capture(AUTH_CONSENT_INIT);
        setTimeout(() => {
            this.personalScopeLabels = this.translateScopeLabels(this.personalScopes);
            localizationService.language$.subscribe(() => {
                this.personalScopeLabels = this.translateScopeLabels(this.personalScopes);
                this.setExternalOrgName();
                if (this.hasAuthorityConfirmationScope) {
                    this.translateAuthorityMatrixConfirmationSignableNames();
                } else if (this.hasAuthorityDeactivationScope || this.hasInternalAuthorityDeactivationScope) {
                    this.translateAuthorityMatrixDeactivationSignableNames();
                }
            });
            const hasProviderScopes = this.scopes.some(scope => this.providerScopes.some(scopeToFilter => scope.startsWith(scopeToFilter)));
            if (this.hasSignScope) {
                analyticsService.capture(ESIGN_INIT);
                this.signables = oauthHolderService.consentData.data.signables;
            }
            if (hasProviderScopes) {
                this.groupScopeByProvider();
                localizationService.language$.subscribe(() => {
                    this.groupScopeByProvider();
                });
            }
            if (!this.isAuthorityFlow) {
                this.visibleSections.push('personal');
                if (this.hasProviderSection) {
                    this.visibleSections.push('banks');
                }
                if (this.hasSignScope) {
                    this.visibleSections.push('sign');
                }
            }

            if (this.hasAuthorityConfirmationScope) {
                this.title = 'i18n_auth_approveAuthorityMatrix';
                this.setExternalOrgName();
                this.translateAuthorityMatrixConfirmationSignableNames();
            } else if (this.hasAuthorityDeactivationScope || this.hasInternalAuthorityDeactivationScope) {
                this.title = 'i18n_auth_deactivateAuthorityMatrix';
                this.setExternalOrgName();
                this.translateAuthorityMatrixDeactivationSignableNames();
                this.buttonAcceptColor = 'accent';
            } else if (this.openidOnly) {
                this.title = 'i18n_auth_consentTitleOpenId';
            } else {
                this.title = this.hasSignScope ? 'i18n_auth_consentNewTitleSign' : 'i18n_auth_consentNewTitle';
            }

            this.buttonAcceptLabel = this.hasAuthorityConfirmationScope
                ? 'i18n_auth_buttonSignAndApprove'
                : this.hasAuthorityDeactivationScope || this.hasInternalAuthorityDeactivationScope
                    ? 'i18n_auth_buttonDeactivateAuthorityMatrix'
                    : this.hasSignScope && this.hasPersonalScope
                        ? 'i18n_auth_buttonSignAndAccept'
                        : this.hasSignScope
                            ? 'i18n_auth_buttonSign'
                            : 'i18n_auth_buttonConfirm';
            this.buttonRejectLabel = this.isAuthorityFlow
                ? 'i18n_auth_buttonRejectSign'
                : 'i18n_auth_buttonDecline';
            this.Spinner.hide(this.INITIAL_SPINNER_ID);
        }, 150);

        if (this.authHolderService.resetEsign) {
            this.esignConsent(true);
        }
    }

    get hasProviderSection(): boolean {
        return this.providersToScopes.length > 0;
    }

    get legalEntityName(): string {
        return (this.language === 'ru' ? this.legalEntity?.nameRu : this.legalEntity?.nameKz) || this.legalEntity?.nameRu;
    }

    get taxIncomeOrgName(): string {
        const data = this.oauthHolderService.consentData.data;
        return (this.language === 'ru' ? data?.taxIncomeOrgNameRu : data?.taxIncomeOrgNameKz) || data?.taxIncomeOrgNameRu;
    }

    setExternalOrgName(): void {
        this.externalOrgName = this.language === 'ru' ?
            this.oauthHolderService.externalOrgNameRu :
            this.oauthHolderService.externalOrgNameKk;
    }

    translateAuthorityMatrixConfirmationSignableNames(): void {
        this.signables.forEach(signable => {
            signable.translatedName = this.localizationService.getLocalizedKey('i18n_auth_consentPowerOfAttorneySignableName', {
                iin: signable.name.split(' ')[3],
            });
        });
    }

    translateAuthorityMatrixDeactivationSignableNames(): void {
        this.signables.forEach(signable => {
            signable.translatedName = this.localizationService.getLocalizedKey('i18n_auth_consentPowerOfAttorneyDeactivationSignableName', {
                iin: signable.name.split(' ')[5],
            });
        });
    }

    acceptConsent(approved: boolean): void {
        if (this.processing) {
            return;
        }
        this.processing = true;
        const spinnerId = approved ? 'submit' : 'reject';
        Spinner.show(spinnerId);
        if (this.hasSignScope) {
            this.esignConsent(approved);
        } else {
            this.consentRequest(approved, 'consent').subscribe((response) => this.redirectOnAccept(response));
        }
    }

    viewDocument(signable: Signable): void {
        const link = document.createElement('a');
        link.href = signable.link.toString();
        link.click();
    }

    viewCoidConsent = (): void => {
        this.authSessionApiService.getCoidConsentAgreement()
            .subscribe(agreement => downloadBase64(agreement.content, agreement.contentType, agreement.name));
    };

    private esignConsent(approved: boolean): void {
        if (approved) {
            this.eSignDialogService.openDialog({
                    esign: this.oauthHolderService.authSessionData.esign as EsignResponse,
                    hasOrgSignScope: this.hasOrgSignScope,
                    authorityMatrixId: this.oauthHolderService.authSessionData.authorityMatrixId,
                    headFullName: this.oauthHolderService.authSessionData.headFullName,
                    headPhone: this.oauthHolderService.authSessionData.headPhone,
                    headEmail: this.oauthHolderService.authSessionData.headEmail,
                    oauthSessionId: this.oauthHolderService.oauthSessionId,
                    phoneNumber: this.oauthHolderService.authSessionData.phone,
                    target: 'sign',
                },
            ).pipe(
                mergeMap((result) => {
                    if (result.error) {
                        throw result.error;
                    } else if (!result.success) {
                        if (result.resetPassword) {
                            this.authSessionApiService.getSecondFactorToken().subscribe(tokenResponse => {
                                this.authHolderService.storeSecondFactorToken(tokenResponse.secondFactorToken);
                                this.authHolderService.storeResetEsign();
                                this.routerService.navigate(Path.CODE);
                            });
                        }
                        return EMPTY;
                    }
                    this.captureEsignEvent(ESIGN_SIGN_SUCCEED);
                    return this.consentRequest(approved);
                }),
                finalize(() => {
                    this.processing = false;
                }),
            ).subscribe({
                next: (oauthResponse) => {
                    if (this.hasAuthorityConfirmationScope) {
                        this.routerService.navigate(Path.AUTH_FINISH, {
                            reason: FinishPageReason.AUTHORITY_MATRIX_ACCEPTED,
                        });
                    } else if (this.hasAuthorityDeactivationScope || this.hasInternalAuthorityDeactivationScope) {
                        this.routerService.navigate(Path.AUTH_FINISH, {
                            reason: FinishPageReason.AUTHORITY_MATRIX_DEACTIVATED,
                        });
                    } else {
                        this.redirectOnAccept(oauthResponse);
                    }
                },
            });

        } else {
            this.captureEsignEvent(ESIGN_SIGN_REJECT, {reason: 'esign_consent'});
            if (this.hasInternalAuthorityDeactivationScope || this.hasAuthorityDeactivationScope) {
                this.oauthService.reportConsentRejected().subscribe(() => this.routerService.navigate(Path.AUTH_FINISH, {
                    reason: FinishPageReason.AUTHORITY_MATRIX_DEACTIVATION_REJECTED,
                }));
            } else {
                const flow$ = this.hasAuthorityConfirmationScope
                    ? this.authorityMatrixApiService.rejectAuthorityMatrix().pipe(
                        switchMap(() => this.consentRequest(approved, 'esign_consent')),
                    ) : this.consentRequest(approved, 'esign_consent');

                flow$.subscribe(
                    response => {
                        // When a request is rejected, this.consentRequest() would always return an 'access_denied'
                        // error that would be handled on ErrorHandler level. But I'm not sure if it's always the case,
                        // so let's leave this positive handler for now
                        if (this.hasAuthorityConfirmationScope) {
                            this.routerService.navigate(Path.AUTH_FINISH, {
                                reason: FinishPageReason.AUTHORITY_MATRIX_REJECTED,
                            });
                        } else {
                            this.redirectOnAccept(response);
                        }
                    },
                    error => {
                        if (this.hasAuthorityConfirmationScope && toBusinessOrNull(error)?.error === BusinessErrorCode.access_denied) {
                            this.routerService.navigate(Path.AUTH_FINISH, {
                                reason: FinishPageReason.AUTHORITY_MATRIX_REJECTED,
                            });
                        } else {
                            throw error;
                        }
                    });
            }
        }
    }

    private consentRequest(approved: boolean, reason?: string): Observable<OauthResponse> {
        const consentData = this.oauthHolderService.consentData;
        if (approved) {
            this.analyticsService.capture(AUTH_CONSENT_AGREED);
        } else {
            this.analyticsService.capture(AUTH_REJECT, {reason: reason});
        }
        const submit$ = this.oauthServiceApiService.submitConsent(
            approved ? consentData.scopes : [],
            consentData.state,
            consentData.clientId,
        );
        if (approved) {
            return submit$.pipe(
                switchMap(response => this.oauthService.reportConsentAccepted().pipe(map(() => response))),
            );
        }
        return this.oauthService.reportConsentRejected().pipe(switchMap(() => submit$));
    }

    private redirectOnAccept(response: OauthResponse): void {
        const completeAuth = (): void => {
            this.analyticsService.capture(AUTH_SUCCEED);
            this.analyticsService.finishSession();
        };

        if (this.oauthHolderService.authSessionData.autoExchangeAuthCode) {
            this.autoExchangeTokenService.exchangeAuthCode({
                code: response.code,
                redirectUri: response.redirectUri,
                grantType: 'authorization_code',
            }).subscribe(() => {
                completeAuth();
                this.routerService.navigate(Path.AUTH_FINISH, {reason: FinishPageReason.AUTO_EXCHANGE_TOKEN});
            });
        } else {
            completeAuth();
            this.oauthService.redirect(response.redirectUri, response.code, response.state);
        }
    }

    private translateScopeLabels(scopes: string[]): string[] {
        return scopes.map(scope => {
            const scopeKey = scope.toLowerCase().split('_')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join('');
            return this.localizationService.getLocalizedKey(`i18n_auth_scope#${scopeKey}`);
        });
    }

    private groupScopeByProvider(): void {
        const providerScopes = this.scopes.filter(scope => this.providerScopes.some(scopeToFilter => scope.startsWith(scopeToFilter)));
        const idsToScope = providerScopes.map(scope => {
            const indexOfScopeDelimiter = scope.indexOf('.');
            const scopeName = scope.slice(0, indexOfScopeDelimiter);
            const idsString = scope.slice(indexOfScopeDelimiter + 1);
            const ids = idsString.split(',');
            return {scopeName, ids};
        });

        const groupedByScopes = new Map<string, ProvidersToScopes>();
        const providers = (this.oauthHolderService.consentData.data.providers as ProviderInfo[] || []);
        this.allProvidersNames = providers.map(provider => provider.name).join(', ');
        providers
            .map(provider => {
                const scopes = idsToScope
                    .filter((result) => result.ids.includes(provider.id))
                    .map(result => result.scopeName)
                    .sort((a, b) => {
                        const indexA = this.providerScopes.indexOf(a);
                        const indexB = this.providerScopes.indexOf(b);
                        return indexA - indexB;
                    });
                return {provider: provider, scopes};
            })
            .sort((a, b) => b.scopes.length - a.scopes.length)
            .forEach(item => {
                const scopesKey = item.scopes.sort().join(',');
                if (!groupedByScopes.has(scopesKey)) {
                    const scopeLabels = this.translateScopeLabels(item.scopes);
                    groupedByScopes.set(scopesKey, {providers: [], scopes: scopeLabels.join(', '), scopeLabels: scopeLabels});
                }
                groupedByScopes.get(scopesKey)?.providers.push(item.provider);
            });
        this.providersToScopes = Array.from(groupedByScopes.values()).map(value => {
            return {
                providersNames: value.providers.map(provider => provider.name).join(', '),
                providers: value.providers,
                scopes: value.scopes,
                scopeLabels: value.scopeLabels,
            };
        });
    }

    private captureEsignEvent(name: string, addProps: Record<string, string> = {}): void {
        this.analyticsService.capture(name, {
            signature_type: this.hasOrgSignScope ? 'organization_esign' : 'user_esign',
            matrix: this.hasAuthorityConfirmationScope.toString(),
            ...addProps,
        });
    }

}

export interface ProvidersToScopes {
    providersNames?: string;
    providers: ProviderInfo[];
    scopes: string;
    scopeLabels: string[];
}

export interface Signable {
    id: string;
    name: string;
    translatedName: string;
    link: URL;
}

interface ProviderInfo {
    id: string;
    name: string;
    logoUrl?: string;
}

type ConsentSection = 'personal' | 'banks' | 'sign';
